/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import axios from 'axios';
import type { AxiosRequestConfig, AxiosResponse } from 'axios';
import { catchError, from, lastValueFrom, tap } from 'rxjs';

import { clearMessages, addMessages, getMessages } from '../messages/index';
import PubSubUtils from '../utils/pubsub-utils';
import constants from '../constants';
import RouteParser from '../router/route-parser';
import Debugger from '../debug/debugger';
import type { Interceptor, FetchOptions } from '../globals';

import fetchErrorCallback from './fetch-errorHandler';
import FetchStatus from './fetchStatus';
import { isExternalUrl, isUpdateSessionRequest } from './utils';
import HeaderProcessor from './header-processor';
import InactivityTimeout from './inactivityTimeout';

export const UPDATE_SESSION = 'updateSession';

const activeRequestCount: { [key: string]: any } = {};
const fetchStatus = new FetchStatus();
export const newTimeout = new InactivityTimeout();

let interceptor: { [key: string]: any } = {};
let defaultAuthInfo: { [key: string]: any } = {};
let defaultAuthConfig = {};
let httpErrorStatusCode: string | null = '';

export const setInterceptor = (interceptorObj: Interceptor) => {
  interceptor = interceptorObj;
};

const publishFetchSuccessEvent = (data: any) => {
  /* publishing event to notify fetch success */
  PubSubUtils.publish(constants.PUB_SUB_EVENTS.FETCH_SUCCESS, data);
};

const publishFetchFailureEvent = (data: any) => {
  /* publishing event to notify fetch failure */
  PubSubUtils.publish(constants.PUB_SUB_EVENTS.FETCH_FAILURE, data);
};

const getWithCredentials = (authInfo: { [key: string]: any }) => {
  if (authInfo?.authType === 'OAuth2.0' && authInfo.secureCookie) {
    return true;
  }
  if (authInfo?.authType === 'custom' && authInfo.customCookies) {
    return true;
  }
  return undefined;
};

export type Config = {
  /** request method(GET, POST, PUT, PATCH, DELETE) to be used when making the request. Default is 'GET' */
  method: AxiosRequestConfig['method'];
  /** custom headers to be sent */
  headers?: AxiosRequestConfig['headers'];
  /** data to be sent as the request body. Only applicable for request methods 'PUT', 'POST', and 'PATCH' */
  body?: AxiosRequestConfig['data'];
  /** number of milliseconds before the request times out. Default is '0' (no timeout) */
  timeout?: AxiosRequestConfig['timeout'];
  /** indicates whether cross-site Access-Control requests. Default is 'false' */
  withCredentials?: AxiosRequestConfig['withCredentials'];
  /** indicates whether or not to send default headers. Default is 'false' */
  withoutDefaultHeaders?: boolean;
  /** allows handling of progress events for uploads */
  onUploadProgress?: AxiosRequestConfig['onUploadProgress'];
  /** allows handling of progress events for downloads */
  onDownloadProgress?: AxiosRequestConfig['onDownloadProgress'];
  /** represents a signal object that allows you to communicate with a fetch request to abort. */
  signal?: AxiosRequestConfig['signal'] | (AxiosRequestConfig['cancelToken'] & { aborted?: string });
  responseType?: AxiosRequestConfig['responseType'];
  adapter?: AxiosRequestConfig['adapter'];
  datapageRequestType?: string;
  topLevelAction?: boolean;
  isExternalUrl?: boolean;
  fetchTimeStamp?: number;
  skipClearErrorMessages?: boolean;
};

/**
 * Seeks url and supporting configuration object for the request to be made.
 * If url is null or undefined, logging a console error.
 * Merge headers passed in config object with the default headers that service broker maintain.
 * Call axios with the prepared config object
 *
 * @param url endpoint url
 * @param config object which supports additional data for network call
 * @param context context object
 * @param options - object containing extra parameter like doNotMergeHttpMessagesForStatusCode and cancelContext.
 * @returns returns the promise object returned by axios
 * @example fetch('http://www.domain.com/employees', {method: 'GET'})
 */
export const fetch = (
  url: string | null | undefined,
  config: Config,
  context: string = 'root',
  options: FetchOptions = {}
  // eslint-disable-next-line sonarjs/cognitive-complexity
): Promise<AxiosResponse<any>> => {
  const {
    doNotMergeHttpMessagesForStatusCode = null,
    cancelContext = context,
    includeRemoteSystemIdIfPresent = true
  } = options;
  httpErrorStatusCode = doNotMergeHttpMessagesForStatusCode;

  if (url === null) {
    /* ≈ to (url === null || url === undefined) */
    window.console.error('Error: url passed to Service Broker fetch is null (or) undefined');
  }
  const { body, signal, responseType, adapter, withoutDefaultHeaders = false, ...restConfig } = config;

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const { Authorization: _, pzCTkn: __, ...headers } = config.headers || {}; // pick Authorization from Default headers
  if (!withoutDefaultHeaders) {
    HeaderProcessor.injectCoreAdditionalHeaders(headers, config.body, context, { includeRemoteSystemIdIfPresent });
  }

  if (cancelContext && activeRequestCount[cancelContext] >= 0) {
    activeRequestCount[cancelContext] += 1;
  } else if (cancelContext && activeRequestCount[cancelContext] === undefined) {
    activeRequestCount[cancelContext] = 1;
  }
  /* merging headers from config object with default service broker object.
        Any duplicates? --> 'config' headers will override the default ones */
  let fetchHeaders = withoutDefaultHeaders
    ? { ...headers }
    : {
        ...HeaderProcessor.getDefaultHeaders(),
        ...HeaderProcessor.getRegisteredHeaders(),
        ...headers,
        context
      };
  restConfig.isExternalUrl = isExternalUrl(url || '');
  restConfig.fetchTimeStamp = Date.now();
  if (
    (url && isUpdateSessionRequest(url, defaultAuthInfo) && fetchStatus.getUpdateSessionStatus()) ||
    !fetchStatus.getUpdateSessionStatus() ||
    restConfig.isExternalUrl
  ) {
    if (isUpdateSessionRequest(url, defaultAuthInfo) && defaultAuthInfo?.authType === 'OAuth2.0') {
      fetchHeaders = { ...headers }; // Skip context and defaultHeaders if Oauth2.0 token request
    }
    const { restApi } = (RouteParser.doesRestApiExist('Logout') && RouteParser.getRestEndPoint('Logout')) || {
      restApi: null
    };
    if (!restApi || (url && !url.endsWith(restApi))) {
      newTimeout.setTimerID(newTimeout.resetInactiveTimeout(newTimeout.getTimerID()));
    }
    return lastValueFrom(
      from(
        axios({
          context,
          ...restConfig,
          url,
          data: body,
          ...(signal &&
            typeof signal.aborted === 'undefined' && {
              cancelToken: signal
            }),
          ...(signal && typeof signal.aborted !== 'undefined' && { signal }),
          responseType,
          adapter,
          headers: fetchHeaders,
          /** we need withCredentials:true if pega-embed uses cookie based authentication */
          withCredentials: getWithCredentials(defaultAuthInfo)
        } as AxiosRequestConfig)
      ).pipe(
        tap(() => {
          const coreHeaders = HeaderProcessor.getCoreHeadersFromContext(fetchHeaders.context);
          if (coreHeaders) publishFetchSuccessEvent({ ...coreHeaders });
        }),
        catchError((error) => {
          // Handling the error case here
          const coreDebugHeaders = fetchHeaders as Record<string, string | undefined>;
          const hasAppDebugID = !!coreDebugHeaders?.[constants.COREHEADER_APP_DEBUG_ID];
          const coreHeaders = hasAppDebugID
            ? {
                debugInfo: {
                  [constants.COREHEADER_APP_DEBUG_ID]: coreDebugHeaders?.[constants.COREHEADER_APP_DEBUG_ID],
                  [constants.COREHEADER_APP_DEBUG_LEVELS]: Debugger.getDebugLevels()
                }
              }
            : HeaderProcessor.getCoreHeadersFromContext(fetchHeaders.context);
          if (coreHeaders) publishFetchFailureEvent({ ...coreHeaders, error });
          throw error;
        })
      )
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  return waitForUpdateSession(url, config, context, fetchStatus);
};

export const waitForUpdateSession = (
  url: string | null | undefined,
  config: any,
  context: any,
  // eslint-disable-next-line @typescript-eslint/no-shadow
  fetchStatus: FetchStatus
): Promise<AxiosResponse<any, any>> => {
  return new Promise((resolve, reject) => {
    PubSubUtils.subscribe(
      UPDATE_SESSION,
      (statusObj: any) => {
        fetchStatus.setUpdateSessionStatus(!statusObj.updateStatus);
        if (statusObj.updateStatus === 'success') {
          fetch(url, config, context)
            .then((response: AxiosResponse<any, any>) => resolve(response))
            .catch((err: any) => reject(err));
        } else {
          reject(statusObj.updateStatus.error);
          // FIXME: what is this scenario as we don't send failure ever for Update_Session
        }
      },
      UPDATE_SESSION
    );
  });
};

const updateActiveRequestCount = (context: string) => {
  if (activeRequestCount[context] > 0) {
    activeRequestCount[context] -= 1;
    if (activeRequestCount[context] === 0) {
      delete activeRequestCount[context];
    }
  }
};

axios.interceptors.request.use(
  (config) => {
    if (interceptor.request) {
      config = interceptor.request(config);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

axios.interceptors.response.use(
  // eslint-disable-next-line sonarjs/cognitive-complexity
  (response: any) => {
    if (response.status === 202 && response.config.headers.context && response.data?.caseInfo?.errorMessage) {
      // handling for 202 response
      const errorMsgArr = [{ type: 'error', message: response.data.caseInfo.errorMessage }];
      let msgContext = response.config.headers.context;
      if (msgContext && msgContext.includes('/workarea')) {
        msgContext = msgContext.substring(0, msgContext.lastIndexOf('/workarea'));
      }
      addMessages({
        messages: errorMsgArr,
        category: 'HTTP',
        context: msgContext
      });
      return Promise.reject(response);
    }

    if (response.config.headers && response.config.headers.context) {
      if (!response.config.skipClearErrorMessages) {
        if (getMessages({ type: 'error', category: 'HTTP', context: response.config.headers.context }).length > 0) {
          clearMessages({
            type: 'error',
            category: 'HTTP',
            context: response.config.headers.context
          });
        }
        if (
          getMessages({
            type: 'error',
            category: response.config.headers.context === constants.APP.APP ? 'PAGE' : '',
            context: response.config.headers.context
          }).length > 0
        ) {
          clearMessages({
            type: 'error',
            category: 'PAGE',
            context: response.config.headers.context
          });
        }
        const shouldClearAppContextErrors = response.config.headers.context
          .slice(response.config.headers.context.lastIndexOf('/') + 1)
          .startsWith('primary');
        if (shouldClearAppContextErrors) {
          if (getMessages({ type: 'error', category: 'HTTP', context: constants.APP.APP }).length > 0) {
            clearMessages({
              type: 'error',
              category: 'HTTP',
              context: constants.APP.APP
            });
          }
          if (getMessages({ type: 'error', category: 'PAGE', context: constants.APP.APP }).length > 0) {
            clearMessages({
              type: 'error',
              category: 'PAGE',
              context: constants.APP.APP
            });
          }
        }
      }
      updateActiveRequestCount(response.config.headers.context);
    }
    if (interceptor.response) {
      response = interceptor.response(response);
    }
    return response;
  },
  (error: any) => {
    if (error.config && error.config.headers && error.config.headers.context) {
      updateActiveRequestCount(error.config.headers.context);
    }

    // This subscription handles for full reauth handling and retrying of the session
    // FIXME: Verify the usecase below
    if (
      error.response &&
      error.response.status &&
      error.response.status === 401 &&
      !error.config.isExternalUrl &&
      (!error.config || !isUpdateSessionRequest(error.config.url, defaultAuthInfo))
    ) {
      PubSubUtils.subscribe(
        UPDATE_SESSION,
        (statusObj: any) => {
          fetchStatus.setUpdateSessionStatus(!statusObj.updateStatus);
        },
        UPDATE_SESSION
      );
    }

    return fetchErrorCallback(
      error,
      fetchStatus,
      defaultAuthInfo,
      error.response && error.response.status && error.response.status === Number(httpErrorStatusCode)
        ? httpErrorStatusCode
        : null
    );
  }
);

/**
 * this function returns number of active request for the given context
 *
 * @param context context object for which the number of active request count is required
 * @returns number of active request for the given context
 */
export const getActiveRequestCountPerContext = (context: any): number => {
  return activeRequestCount[context];
};

/**
 * returns the axios cancelToken source object
 *
 * @returns returns the axios cancelToken source object
 *
 */
export const getCancelTokenSource = () => {
  return axios.CancelToken.source();
};

/**
 * when promise is rejected, this function helps to check if it's because the request was cancelled
 *
 * @param error error object when promise rejects
 * @returns true/false by calling axios.isCancel for given error object
 */
export const isFetchCanceled = (error: { name?: string; message?: string }): boolean => {
  return axios.isCancel(error);
};

export const updateHeaders = (headersObj: { Authorization?: string | null; pzCTkn?: string }) => {
  HeaderProcessor.setDefaultHeaders(headersObj);
};

export const updateAuthInfo = (authInfo: { tokenInfo?: any }) => {
  defaultAuthInfo = {
    ...defaultAuthInfo,
    ...authInfo
  };
};

export const updateAuthConfig = (config: { [key: string]: any }) => {
  defaultAuthConfig = {
    ...defaultAuthConfig,
    ...config
  };
};

export const getAuthConfig = () => {
  return { ...defaultAuthConfig };
};

export const getAuthInfo = () => {
  return { ...defaultAuthInfo };
};

export const resetFetchStatus = () => {
  fetchStatus.setUpdateSessionStatus(false);
};
